import React, { Fragment, useCallback, useEffect, useReducer, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { useDebouncedCallback } from "use-debounce";
import { toast } from "react-toastify";

import axios from "axios";
import ai, { redirectToLogin } from "../../helpers/axios";
import usePolling from "../../helpers/usePolling";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Search from "../search";
import Loader from "../loader";
import { unassignedLeadsColumns } from "./unassigned-leads-list-columns";
import reducer from "./reducer";
import Select from "react-select";

const UnassignedLeadsContent = () => {
  const initialState = {
    loading: true,
    data: [],
    totalLength: 0,
    query: "",
    countPerPage: 10,
    currentPage: 1,
    sortBy: "createdAt",
    sortDirection: "desc",
    selectedRows: [],
    allSelected: false,
    ownerOptions: [],
    selectedOwner: null,
  };
  const { getAccessTokenSilently } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);

  const getData = async (query, loadSilently) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let loadingStatus = !loadSilently;

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    try {
      const token = await getAccessTokenSilently().catch(() => {
        redirectToLogin();
      });

      if (!token) return;

      const { data } = await ai.auth(token).get("/api/dashboard/unassigned-leads", {
        signal,
        params: {
          query: query.trim(),
          page: state.currentPage,
          countPerPage: state.countPerPage,
          sortBy: state.sortBy,
          sortDirection: state.sortDirection,
        },
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  };

  const loadOwnerOptions = useCallback(async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      redirectToLogin();
    });

    if (!token) return [];

    let { data } = await ai.auth(token).get(`/api/users/roles/sales`, {});

    if (data.users.length >= 1) {
      let results = data.users.map((user) => {
        return { value: user.user_id, label: user.name, email: user.email };
      });

      dispatch({ type: "fieldChanged", field: "ownerOptions", value: results });

      let ownerFilterOptions = [...results];
      ownerFilterOptions.unshift({ label: "Unassigned", value: null });
      dispatch({
        type: "fieldChanged",
        field: "ownerFilterOptions",
        value: ownerFilterOptions,
      });

      return results;
    } else {
      return [];
    }
  }, [getAccessTokenSilently]);

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const debouncedGetData = useDebouncedCallback(
    (query) => {
      getData(query);
    },
    500,
  );

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });

    debouncedGetData(query);
  };

  const handleSort = (column, sortDirection) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  const handleSelection = function (rowData) {
    dispatch({
      type: "rowSelection",
      payload: {
        selectedRows: rowData.selectedRows,
        allSelected: rowData.allSelected,
      },
    });
  };

  const preSelectCriteria = (row) => {
    let selectedRows = [...state.selectedRows];

    selectedRows = selectedRows.filter(
      (selectedRow) => selectedRow._id === row._id,
    );

    return selectedRows.length;
  };

  useEffect(() => {
    getData(state.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.countPerPage, state.sortBy, state.sortDirection]);

  usePolling(
    () => getData(state.query, true),
    60 * 1000,
    [state.query, state.currentPage, state.countPerPage, state.sortBy, state.sortDirection],
  );

  const handleOwnerSelection = async (selectedOwner) => {
    dispatch({
      type: "fieldChanged",
      field: "selectedOwner",
      value: selectedOwner,
    });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      redirectToLogin();
    });

    if (!token) return;

    // hit api endpoint to update assignment -leads

    let leadIds = state.selectedRows.map((row) => row._id);

    let { data } = await ai.auth(token).put(`/api/leads/bulkAssign`, {
      leadIds: leadIds,
      assigneeId: selectedOwner.value,
    }).catch((e) => console.error(e));

    if (data) {
      const tripsUpdated = data?.propagation?.tripsUpdated || 0;
      const contractsUpdated = data?.propagation?.contractsUpdated || 0;
      const toastOpts = { position: "top-right", autoClose: 6000 };
      if (tripsUpdated > 0 || contractsUpdated > 0) {
        toast.success(
          `Lead assigned to ${selectedOwner.label}. Updated ${tripsUpdated} trip(s) and ${contractsUpdated} contract(s).`,
          toastOpts
        );
      } else {
        toast.success(`Lead assigned to ${selectedOwner.label}.`, toastOpts);
      }

      getData(state.query);

      dispatch({
        type: "fieldChanged",
        field: "selectedRows",
        value: [],
      });

      dispatch({
        type: "fieldChanged",
        field: "selectedOwner",
        value: null,
      });

      return data;
    }

    return selectedOwner;
  };

  useEffect(() => {
    loadOwnerOptions();
  }, [loadOwnerOptions]);

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1="Unassigned Leads"
        rightColumn={
          <div
            id="unassigned-leads-actions-row"
            className="header-actions-row base-header-actions-row has-delete-btn opi-header"
          >
            <Select
              className="owner-select"
              classNamePrefix="select"
              value={state.selectedOwner}
              options={state.ownerOptions}
              placeholder="Assign Lead"
              isDisabled={state.selectedRows.length > 0 ? "" : "disabled"}
              onChange={handleOwnerSelection}
            />

            <Search
              placeholder="Search"
              onSearch={handleSearch}
              query={state.query}
              className="unassigned-leads-search"
            />
          </div>
        }
      />

      <div className="server-pagination-table-container trips-list-table-container unassigned-leads-table">
        <StickyHeaderDataTable
          onSelectedRowsChange={handleSelection}
          columns={unassignedLeadsColumns}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[5, 10]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.loading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
          selectableRowSelected={preSelectCriteria}
          selectableRows
        />
      </div>
    </Fragment>
  );
};

export default UnassignedLeadsContent;
